import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { jwtDecode } from "jwt-decode";
import {
  buildClientThemeFromSettings,
  mergeFlowSettings,
} from "src/utils/clientThemeFromSettings";
import { makePersistable } from "mobx-persist-store";
import logger from "src/utils/logger";

class ApplicationStore {
  workflowNextServiceResponse = {}

  instructionList = {
    prop_0: "",
    prop_active: 0,
    is_completed: false,
    prop_1: "",
    prop_2: "",
    prop_3: "",
    prop_4: "",
  };

  loading = false;

  description = "";

  img = null;

  backImg = null;

  selfie = null;

  verifications = {
    hasIdCard: false,
    hasDriverLicence: false,
    hasPassport: false,
    hasResidencePermit: false
  };

  verificationSteps = {
    activeStep: '',
    isCompleted: false
  };

  country = "";

  aidCode = "";

  eidCode = "";

  documentSurface = {
    front: false,
    back: false
  };

  docsType = "";

  pageOrder = [];

  access_token = "";

  client_id = "";

  modelCotrolName = "";

  previousRoute = {
    continueOnAnotherDevice: "",
    continueOnAnotherDeviceSmsOrEmail: "",
    cameraError: ""
  };

  email = "";

  conditionContext = {}

  cities = [];

  project = 0;

  type = ""

  workflowId = 0;

  livenessImages = []

  applicationErrorObj = {
    header: "",
    description: "",
    buttonList: [],
    footerDescription: "",
    footerLink: "",
    footerLinkText: "",
    listItems: []
  };

  isEddForm = false

  eddServiceResponse = {}

  isPeriodicForm = false;

  periodicServiceResponse = {};

  clientFormOriginalFieldCount = 0

  lastContextOfStep = {}

  addressData = {
    addressLine1: "",
    postcode: "",
    city: "",
    country: ""
  }

  accountStatus = null

  pepStatus = null

  inquiry_status = null

  sessionThemeSettings = null

  get clientBranding() {
    try {
      let jwtSettings = null;
      if (this.access_token) {
        const decoded = jwtDecode(this.access_token);
        jwtSettings = decoded?.settings ?? null;
      }
      const merged = mergeFlowSettings(this.sessionThemeSettings, jwtSettings);
      if (!merged) {
        return null;
      }
      return buildClientThemeFromSettings(merged);
    } catch {
      return null;
    }
  }

  constructor() {
    makeObservable(this, {
      workflowNextServiceResponse: observable,
      setWorkflowNextServiceResponse: action,
      instructionList: observable,
      description: observable,
      loading: observable,
      setLoading: action,
      setInstructionList: action,
      setDescription: action,
      img: observable,
      setImg: action,
      backImg: observable,
      setBackImg: action,
      selfie: observable,
      setSelfie: action,
      verificationSteps: observable,
      setVerificationSteps: action,
      verifications: observable,
      setVerifications: action,
      country: observable,
      setCountry: action,
      aidCode: observable,
      setAidCode: action,
      eidCode: observable,
      setEidCode: action,
      documentSurface: observable,
      setDocumentSurface: action,
      docsType: observable,
      setDocsType: action,
      pageOrder: observable,
      setPageOrder: action,
      access_token: observable,
      setAccessToken: action,
      client_id: observable,
      setClientId: action,
      modelCotrolName: observable,
      setModelCotrolName: action,
      previousRoute: observable,
      setPreviousRoute: action,
      setContinueOnAnotherDevice: action,
      setContinueOnAnotherDeviceSmsOrEmail: action,
      setCameraError: action,
      email: observable,
      setEmail: action,
      conditionContext: observable,
      setConditionContext: action,
      cities: observable,
      setCities: action,
      project: observable,
      setProject: action,
      type: observable,
      setType: action,
      workflowId: observable,
      setWorkflowId: action,
      setInprogressServiceStatus: action,
      livenessImages: observable,
      setLivenessImages: action,
      applicationErrorObj: observable,
      setApplicationErrorObj: action,
      resetApplicationErrorObj: action,
      isEddForm: observable,
      setIsEddForm: action,
      eddServiceResponse: observable,
      setEddServiceResponse: action,
      clientFormOriginalFieldCount: observable,
      setClientFormOriginalFieldCount: action,
      periodicServiceResponse: observable,
      setPeriodicServiceResponse: action,
      isPeriodicForm: observable,
      setIsPeriodicForm: action,
      lastContextOfStep: observable,
      setLastContextOfStep: action,
      addressData: observable,
      setAddressData: action,
      accountStatus: observable,
      setAccountStatus: action,
      pepStatus: observable,
      setPepStatus: action,
      inquiry_status: observable,
      setInquiryStatus: action,
      sessionThemeSettings: observable,
      setSessionThemeSettings: action,
      applyAidAuthBootstrap: action,
      resetStore: action,
      clientBranding: computed,
    });

    makePersistable(this, {
      name: "ApplicationStore",
      properties: [
        "workflowNextServiceResponse",
        "instructionList",
        "loading",
        "description",
        "img",
        "backImg",
        "selfie",
        "verifications",
        "verificationSteps",
        "country",
        "aidCode",
        "eidCode",
        "documentSurface",
        "docsType",
        "pageOrder",
        "access_token",
        "client_id",
        "modelCotrolName",
        "previousRoute",
        "email",
        "conditionContext",
        "cities",
        "project",
        "type",
        "cities",
        "workflowId",
        "livenessImages",
        "applicationErrorObj",
        "isEddForm",
        "eddServiceResponse",
        "clientFormOriginalFieldCount",
        "isPeriodicForm",
        "periodicServiceResponse",
        "lastContextOfStep",
        "addressData",
        "accountStatus",
        "pepStatus",
        "inquiry_status",
        "sessionThemeSettings",
      ],
      storage: window.sessionStorage
    }).then(() => {
      logger.log("Store has been hydrated");
    });
  }

  setWorkflowNextServiceResponse = (response) => {
    this.workflowNextServiceResponse = response;
  };

  setInstructionList(instructionList) {
    this.instructionList = instructionList;
  }

  setDescription(description) {
    this.description = description;
  }

  setLoading(loading) {
    this.loading = loading;
  }

  setImg(img) {
    this.img = img;
  }

  setBackImg(img) {
    this.backImg = img;
  }

  setSelfie(selfie) {
    this.selfie = selfie;
  }

  setVerificationSteps = (steps) => {
    this.verificationSteps = steps;
  }

  setVerifications = (verification) => {
    this.verifications = verification;
  }

  setDocumentSurface = (surface) => {
    this.documentSurface = surface;
  }

  setCountry = (country) => {
    this.country = country;
  }

  setAidCode = (code) => {
    this.aidCode = code;
  }

  setEidCode = (value) => {
    this.eidCode = value ?? "";
  }

  setDocsType = (type) => {
    this.docsType = type;
  }

  setPageOrder(order) {
    this.pageOrder = [...order];
  }

  setAccessToken = (token) => {
    this.access_token = token;
  }

  setClientId = (id) => {
    this.client_id = id;
  }

  setModelCotrolName = (name) => {
    this.modelCotrolName = name;
  }

  setPreviousRoute(route) {
    this.previousRoute = route;
  }

  setContinueOnAnotherDevice(value) {
    this.previousRoute.continueOnAnotherDevice = value;
  }

  setContinueOnAnotherDeviceSmsOrEmail(value) {
    this.previousRoute.continueOnAnotherDeviceSmsOrEmail = value;
  }

  setCameraError(value) {
    this.previousRoute.cameraError = value;
  }

  setEmail = (email) => {
    this.email = email;
  }

  setConditionContext = (context) => {
    this.conditionContext = {
      ...this.conditionContext,
      ...context
    };
  }

  setCities = (cities) => {
    this.cities = cities;
  }

  setProject = (project) => {
    this.project = project;
  }

  setType = (type) => {
    this.type = type;
  }

  setWorkflowId = (id) => {
    this.workflowId = id;
  }

  setLivenessImages = (image) => {
    this.livenessImages = [...this.livenessImages, image];
  }

  resetLivenessImages = () => {
    this.livenessImages = []
  }

  areAllNodesCompleted() {
    if (this.pageOrder.length > 0) {
      return this.pageOrder.every((x) => x.isCompleted);
    }
    return false;
  }

  setInprogressServiceStatus = (serviceName, status, reason = null) => {
    if (!Array.isArray(this.workflowNextServiceResponse.inprogress_services)) {
      return;
    }

    const updatedServices = this.workflowNextServiceResponse.inprogress_services.map(service => {
      if (service.microservice_id === serviceName) {
        return {
          ...service,
          status,
          reason
        };
      }
      return service;
    });

    if (updatedServices.some((service, index) =>
      service !== this.workflowNextServiceResponse.inprogress_services[index]
    )) {
      this.workflowNextServiceResponse = {
        ...this.workflowNextServiceResponse,
        inprogress_services: updatedServices
      };
    }
  }

  setApplicationErrorObj = (obj) => {
    this.applicationErrorObj = { ...obj };
  };

  setIsEddForm = (edd) => {
    this.isEddForm = edd
  }

  setEddServiceResponse = (response) => {
    this.eddServiceResponse = response;
  };

  setIsPeriodicForm = (value) => {
    this.isPeriodicForm = value;
  }

  setPeriodicServiceResponse = (response) => {
    this.periodicServiceResponse = response;
  }

  setClientFormOriginalFieldCount = (count) => {
    this.clientFormOriginalFieldCount = count;
  };

  setLastContextOfStep = (context) => {
    this.lastContextOfStep = context;
  };

  setAddressData = (data) => {
    this.addressData = { ...this.addressData, ...data };
  };

  setAccountStatus = (status) => {
    this.accountStatus = status;
  };

  setPepStatus = (status) => {
    this.pepStatus = status;
  };

  setInquiryStatus = (status) => {
    this.inquiry_status = status;
  };

  setSessionThemeSettings = (value) => {
    this.sessionThemeSettings =
      value && typeof value === "object" ? { ...value } : null;
  };

  /** Session theme + token + aid in one MobX transaction so `clientBranding` recomputes once. */
  applyAidAuthBootstrap = ({ sessionThemeSettings, access_token, aidCode }) => {
    runInAction(() => {
      this.setSessionThemeSettings(sessionThemeSettings ?? null);
      this.setAccessToken(access_token);
      if (aidCode != null) {
        this.setAidCode(aidCode);
      }
    });
  };

  resetApplicationErrorObj = () => {
    this.applicationErrorObj = {
      header: "",
      description: "",
      buttonList: [],
      footerDescription: "",
      footerLink: "",
      listItems: []
    };
  };

  resetStore = () => {
    this.setWorkflowNextServiceResponse({});
    this.setInstructionList({
      prop_active: 0,
      is_completed: false
    });
    this.setLoading(false);
    this.setDescription("");
    this.setImg(null);
    this.setBackImg(null);
    this.setSelfie(null);
    this.setVerifications({
      hasIdCard: false,
      hasDriverLicence: false,
      hasPassport: false,
      hasResidencePermit: false
    });
    this.setVerificationSteps({
      activeStep: '',
      isCompleted: false
    });
    this.setCountry("");
    this.setAidCode("");
    this.setEidCode("");
    this.setDocumentSurface({
      front: false,
      back: false
    });
    this.setDocsType("");
    this.setPageOrder([]);
    this.setAccessToken("");
    this.setClientId("");
    this.setModelCotrolName("");
    this.setPreviousRoute({
      continueOnAnotherDevice: "",
      continueOnAnotherDeviceSmsOrEmail: "",
      cameraError: ""
    });
    this.setEmail("");
    this.setConditionContext({});
    this.setCities([]);
    this.setProject(0);
    this.setType("");
    this.setWorkflowId(0);
    this.resetLivenessImages();
    this.resetApplicationErrorObj();
    this.setIsEddForm(false);
    this.setEddServiceResponse({});
    this.setIsPeriodicForm(false);
    this.setPeriodicServiceResponse({});
    this.setClientFormOriginalFieldCount(0);
    this.setLastContextOfStep({});
    this.setAddressData({
      addressLine1: "",
      postcode: "",
      city: "",
      country: ""
    });
    this.setAccountStatus(null);
    this.setPepStatus(null);
    this.setInquiryStatus(null);
    this.setSessionThemeSettings(null);
  }

}

export const applicationStore = new ApplicationStore();
